import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import './i18n'; // Initialize i18n
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import { TodayDashboard } from './pages/TodayDashboard';
import { EveningDebrief } from './pages/EveningDebrief';
import MundusDetail from './pages/MundusDetail';
import MundusAnalytics from './pages/MundusAnalytics';
import Achievements from './pages/Achievements';
import PaymentPlans from './pages/PaymentPlans';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Preferences from './pages/Preferences';
import { Simulation } from './pages/Simulation';
import ProtectedRoute from './components/ProtectedRoute';
import { Notifications } from './components/Notifications';
import { AchievementUnlockModal } from './components/AchievementUnlockModal';
import { useAuthStore } from './stores/auth.store';
import { DemoTimer } from './components/DemoTimer';
import { useNotificationStore } from './stores/notification.store';
import { connectSocket, disconnectSocket } from './lib/socket';
import { useMetricUpdates, useReportReady, useReportProgress, useAchievementUnlock } from './hooks/useSocket';
import { Achievement } from './types/mundus';

function App() {
  const { isAuthenticated, accessToken } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const [unlockedAchievement, setUnlockedAchievement] = useState<Achievement | null>(null);
  const [isAchievementModalOpen, setIsAchievementModalOpen] = useState(false);

  // Connect Socket.IO when authenticated
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      connectSocket(accessToken).catch((error) => {
        console.error('Failed to connect socket:', error);
      });

      return () => {
        disconnectSocket();
      };
    }
  }, [isAuthenticated, accessToken]);

  // Listen for real-time metric updates
  useMetricUpdates((data) => {
    addNotification({
      type: 'success',
      title: 'Metric Updated',
      message: `${data.metric.name} updated to ${data.metric.currentValue} ${data.metric.unit}`,
      duration: 4000,
    });
  });

  // Listen for report progress
  useReportProgress((data) => {
    addNotification({
      type: 'info',
      title: 'Report Generation',
      message: data.message,
      duration: 3000,
    });
  });

  // Listen for report ready
  useReportReady((data) => {
    addNotification({
      type: 'success',
      title: 'Report Ready!',
      message: `Your daily report is ready with a score of ${data.report.overallScore}`,
      duration: 6000,
    });
  });

  // Listen for achievement unlocks
  useAchievementUnlock((data) => {
    const achievement = data.achievement;

    // Show toast notification
    addNotification({
      type: 'success',
      title: 'Achievement Unlocked!',
      message: `${achievement.title} - ${achievement.points} points`,
      duration: 5000,
    });

    // Show unlock modal
    setUnlockedAchievement(achievement);
    setIsAchievementModalOpen(true);
  });

  return (
    <Router>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1a1a2e',
            color: '#e0e0e0',
            border: '1px solid #3d3d5c',
            fontFamily: 'monospace',
          },
          success: {
            duration: 4000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#1a1a2e',
            },
            style: {
              border: '1px solid #10b981',
            },
          },
          error: {
            duration: 6000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#1a1a2e',
            },
            style: {
              border: '1px solid #ef4444',
            },
          },
          loading: {
            iconTheme: {
              primary: '#3b82f6',
              secondary: '#1a1a2e',
            },
            style: {
              border: '1px solid #3b82f6',
            },
          },
        }}
      />
      <Notifications />
      <AchievementUnlockModal
        achievement={unlockedAchievement}
        isOpen={isAchievementModalOpen}
        onClose={() => {
          setIsAchievementModalOpen(false);
          setUnlockedAchievement(null);
        }}
      />
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />}
        />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <TodayDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/munduses"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mundus/:id"
          element={
            <ProtectedRoute>
              <MundusDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mundus/:id/analytics"
          element={
            <ProtectedRoute>
              <MundusAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mundus/:id/achievements"
          element={
            <ProtectedRoute>
              <Achievements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <ProtectedRoute>
              <PaymentPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preferences"
          element={
            <ProtectedRoute>
              <Preferences />
            </ProtectedRoute>
          }
        />
        <Route
          path="/simulation/:planId"
          element={
            <ProtectedRoute>
              <Simulation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/debrief"
          element={
            <ProtectedRoute>
              <EveningDebrief />
            </ProtectedRoute>
          }
        />

        {/* Backward compat */}
        <Route path="/settings/billing" element={<Navigate to="/settings" replace />} />
        <Route path="/munduses" element={<Navigate to="/dashboard" replace />} />

        {/* Default route */}
        <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />} />

        {/* 404 route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Demo timer — shows countdown for demo users */}
      {isAuthenticated && <DemoTimer />}
    </Router>
  );
}

export default App;
